import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiKey, FiX, FiPlus, FiSlash } from 'react-icons/fi';
import { financeAPI, usersAPI } from '../../services/api';
import * as fs from './financeStyles';

const ACCESS_LEVELS = [['VIEW_ONLY', 'View Only'], ['DATA_ENTRY', 'Data Entry (create/edit drafts)']];
const SCOPE_AREAS = [
  ['ALL', 'All Finance Data'], ['CHART_OF_ACCOUNTS', 'Chart of Accounts'], ['JOURNAL_ENTRIES', 'Journal Entries'],
  ['FISCAL_PERIODS', 'Fiscal Periods'], ['REPORTS', 'Reports'], ['BANK_ACCOUNTS', 'Bank Accounts'],
  ['EXPENSES', 'Expenses'], ['SALES_INVOICES', 'Sales Invoices'], ['INTERCOMPANY', 'Intercompany Transactions'],
  ['TAX_COMPLIANCE', 'Tax Compliance'],
];

const emptyForm = () => ({ granted_to: '', entity: '', access_level: 'VIEW_ONLY', scope_area: 'ALL', reason: '', expires_at: '', mfa_code: '' });

const AccessGrants = () => {
  const [entities, setEntities] = useState([]);
  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, userRes, grantRes] = await Promise.all([
        financeAPI.entities.list(), usersAPI.list(), financeAPI.grants.list(),
      ]);
      const allUsers = userRes.data.results || userRes.data || [];
      setEntities(entRes.data.results || entRes.data || []);
      setUsers(allUsers.filter((u) => !['STUDENT', 'CHAIRMAN', 'CFO'].includes((u.role || '').toUpperCase())));
      setGrants(grantRes.data.results || grantRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load access grants.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { mfa_code, entity, ...rest } = form;
      await financeAPI.grants.create({ ...rest, entity: entity || null, mfa_code });
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to issue grant — check your MFA code.');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (grant) => {
    const reason = window.prompt('Reason for revoking this grant?') || '';
    setBusyId(grant.id);
    setError(null);
    try { await financeAPI.grants.revoke(grant.id, reason); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to revoke grant.'); }
    finally { setBusyId(null); }
  };

  const userName = (id) => { const u = users.find((x) => x.id === id); return u ? (u.username || u.email) : `#${id}`; };
  const entityName = (id) => id ? (entities.find((e) => e.id === id)?.name || `#${id}`) : 'Both entities';

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiKey size={22} />Finance Access Grants</h1>
          <p style={fs.pageSubtitle}>Scoped, time-boxed, revocable finance access for staff who are neither Chairman nor CFO. Never grants the right to post, approve, or reverse money movement.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Grant</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><div style={fs.label}>Grant To</div>
            <select required value={form.granted_to} onChange={(e) => setForm({ ...form, granted_to: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.username || u.email} ({u.role})</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Entity</div>
            <select value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Both entities</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Access Level</div>
            <select value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              {ACCESS_LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Scope Area</div>
            <select value={form.scope_area} onChange={(e) => setForm({ ...form, scope_area: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              {SCOPE_AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Expires At</div><input required type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div style={{ gridColumn: '1 / -1' }}><div style={fs.label}>Reason (required)</div><textarea required rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={{ ...fs.input, width: '100%', resize: 'vertical' }} /></div>
          <div><div style={fs.label}>Your MFA Code (high-risk action)</div><input required maxLength={10} value={form.mfa_code} onChange={(e) => setForm({ ...form, mfa_code: e.target.value })} placeholder="000000" style={{ ...fs.input, width: '100%' }} /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Issuing…' : 'Issue Grant'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : grants.length === 0 ? (
        <div style={fs.emptyState}><FiKey size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No access grants issued yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {grants.map((g) => {
            const busy = busyId === g.id;
            const status = g.revoked_at ? 'REVOKED' : g.is_active ? 'ACTIVE' : 'EXPIRED';
            const color = status === 'ACTIVE' ? fs.colors.green : status === 'REVOKED' ? fs.colors.red : '#888';
            return (
              <div key={g.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{userName(g.granted_to)}</span>
                      <span style={fs.badge(color)}>{status}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      {ACCESS_LEVELS.find((a) => a[0] === g.access_level)?.[1]} · {SCOPE_AREAS.find((s) => s[0] === g.scope_area)?.[1]} · {entityName(g.entity)}
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>“{g.reason}”</div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 11, marginTop: 4 }}>
                      expires {new Date(g.expires_at).toLocaleString()}
                      {g.revoked_at && ` · revoked ${new Date(g.revoked_at).toLocaleString()}${g.revocation_reason ? ` (${g.revocation_reason})` : ''}`}
                    </div>
                  </div>
                  {status === 'ACTIVE' && (
                    <button onClick={() => revoke(g)} disabled={busy} style={fs.dangerButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Revoke</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccessGrants;
